import React, { useEffect, useMemo, useRef } from "react"

import Select from "~/ui-rtk/components/ui/controls/Select"
import { useGetCustomConnectorConfigOptionsQuery } from "~/ui-rtk/api/customConnectorApi"

export interface CustomConnectorConfigFieldSchema {
  format: "select"
  property_label: string
  configuration_path: string
  options_source?: string
  help_text?: string
}

interface CustomConfigFieldProps {
  companyConnectorId: string
  field: CustomConnectorConfigFieldSchema
  value?: string
  onChange: (value: string) => void
  /**
   * Auto-select the first option once the list arrives if `value` is empty
   * or not among the options (e.g. a saved account the latest login cannot
   * reach, which the API rejects).
   * Useful in the post-OAuth setup form; off in the edit-from-info-modal
   * flow so the saved selection is preserved without being clobbered.
   */
  autoSelectFirst?: boolean
}

const CustomConfigField: React.FC<CustomConfigFieldProps> = ({
  companyConnectorId,
  field,
  value,
  onChange,
  autoSelectFirst = false,
}) => {
  const { data: options, isLoading: isLoadingOptions } =
    useGetCustomConnectorConfigOptionsQuery(
      { id: companyConnectorId, source: field.options_source as string },
      { skip: !field.options_source },
    )

  // One-shot: pick the first option the first time the list arrives if
  // nothing's selected or the selection isn't in the list. After that,
  // never overwrite user edits — even if the list re-fetches.
  const hasAutoSelected = useRef(false)
  useEffect(() => {
    if (hasAutoSelected.current) return
    if (!autoSelectFirst) return
    if (!options || options.length === 0) return
    if (value && options.some(opt => opt.value === value)) return
    hasAutoSelected.current = true
    onChange(options[0].value)
  }, [autoSelectFirst, options, value, onChange])

  const selectOptions = useMemo(
    () =>
      (options ?? []).map(opt => ({
        label: opt.hint ? `${opt.label} — ${opt.hint}` : opt.label,
        value: opt.value,
      })),
    [options],
  )

  // A controlled <select> whose value matches no option still shows the
  // first option, while the form holds an empty or stale value: the user
  // sees an account that looks picked and cannot pick it. Show a disabled
  // placeholder instead, so the user must make a real pick.
  const isValueAnOption = selectOptions.some(opt => opt.value === value)
  const displayedOptions = isValueAnOption
    ? selectOptions
    : [
        {
          label: `Select ${field.property_label}...`,
          value: "",
          disabled: true,
        },
        ...selectOptions,
      ]

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-platinum">{field.property_label}</label>

      {isLoadingOptions && (
        <div className="flex justify-center py-2">
          <span className="loading loading-spinner loading-sm text-primary" />
        </div>
      )}

      {!isLoadingOptions && selectOptions.length === 0 && (
        <p className="text-sm text-system-error-400">
          No options available. Please reconnect to grant access.
        </p>
      )}

      {!isLoadingOptions && selectOptions.length > 0 && (
        <Select
          options={displayedOptions}
          value={isValueAnOption ? value : ""}
          onChange={e => onChange(e.target.value)}
        />
      )}

      {field.help_text && (
        <p className="text-xs text-platinum opacity-70">{field.help_text}</p>
      )}
    </div>
  )
}

export default CustomConfigField
